import Dispatcher from "../dispatcher";
import BookStore from "./book-store";
import MarkerStore from "./marker-store";
import moment from "moment";

const HighlightStore = {
  _state: {
    highlights: [],
    subscribedHighlights: []
  },

  isLoading: false,

  // get highlight by book and syncId
  // TODO maybe this could be better/updated to search regardless of book?
  get(isbn, syncId) {
    let book = BookStore.get(isbn);
    return this.combinedHighlights(isbn).find(highlight => {
      return highlight.syncId === syncId;
    });
  },

  // Get all highlights for the given book
  // TODO allow no book to return entire object?
  getAll(isbn) {
    let highlights = this._state[isbn];
    // TODO init the highlight object - right now the book controls this
    highlights = this._state[isbn] = highlights || this._getBlank(isbn);
    return highlights;
  },

  // Get all highlights in a specific range for the given book
  getRange(isbn, startCfi, endCfi) {
    const range = Object.assign({}, this.getAll(isbn));
    const filterRange = highlight => {
      const hightlightCfi = highlight.cfi || highlight.chapterCfi;
      const start = Jigsaw.CFIUtils.compareCFIs(hightlightCfi, startCfi) >= 0;
      const end = Jigsaw.CFIUtils.compareCFIs(hightlightCfi, endCfi) <= 0;
      return start && (end || hightlightCfi.indexOf(endCfi) === 0);
    };
    if (!range.highlightsLoading) {
      range.highlights = range.highlights.filter(filterRange);
      range.subscribedHighlights = range.subscribedHighlights.filter(
        filterRange
      );
    }
    return range;
  },

  getRangeByIndex(isbn, startIndex, endIndex) {
    const range = Object.assign({}, this.getAll(isbn));
    const toNumber = index => index.replace(/\D/g, "");
    const startAsNumber = toNumber(startIndex),
      endAsNumber = toNumber(endIndex);
    const filterRange = highlight =>
      (highlight.startPage >= startIndex && highlight.startPage <= endIndex) ||
      (highlight.startPage >= startAsNumber &&
        highlight.startPage <= endAsNumber);

    if (!range.highlightsLoading) {
      range.highlights = range.highlights.filter(filterRange);
      range.subscribedHighlights = range.subscribedHighlights.filter(
        filterRange
      );
    }
    return range;
  },

  reset(isbn) {
    let highlights = this.getAll(isbn);
    highlights = this._getBlank(isbn);
    this._load(isbn);
    return highlights;
  },

  _getBlank(isbn) {
    let store = this;
    return Object.assign({}, this._state, {
      isbn: isbn,
      _updateState: function(state) {
        store._updateState(this.isbn, state);
      }
    });
  },

  _updateState(isbn, state) {
    // TODO
    this._state[isbn] = $.extend(this._state[isbn], state);
    Dispatcher.emit(Dispatcher.HIGHLIGHTS_CHANGE, isbn);
  },

  _load(isbn) {
    const book = BookStore.get(isbn);
    const highlights = this.getAll(isbn);
    if (book.viewer && !highlights.highlightsLoading && book.viewerIsReady) {
      highlights._updateState({ highlightsLoading: true });
      book.viewer.Book.getHighlights(response => {
        this._processHighlightResponse(isbn, response);
      });
    }
  },

  cancel(isbn) {
    const highlights = this.getAll(isbn);
    highlights._updateState({ highlightsLoading: false });
  },

  _processHighlightResponse(isbn, response) {
    const highlights = this.getAll(isbn);
    if (highlights.highlightsLoading) {
      highlights._updateState({ highlightsLoading: false });
      if (response.isSuccess()) {
        highlights._updateState({ highlightsLoading: false });
        if (response.data) {
          /* highlights, markers, subscribedHighlights, subscribedMarkers */
          highlights._updateState(response.data);
          MarkerStore.__tmpSetMarkers(response.data);
          const combinedMarkers = response.data.subscribedMarkers.concat(
            response.data.markers
          );
          this._regenerateMarkerHash(combinedMarkers);
          this._updateAllHighlightProperties(isbn);
        }
        Dispatcher.emit(Dispatcher.NOTES_UP);
      } else {
        highlights._updateState({
          highlights: [],
          markers: [],
          subscribedMarkers: [],
          subscribedHighlights: [],
          highlightsLoading: false
        });
        Dispatcher.emit(Dispatcher.NOTES_DOWN);
        Fraction.Helpers.toast(
          i18n.t("reader.notebook.notes-highlights-error"),
          { type: "error", dismissable: true }
        );
      }
    }
  },

  // return highlights (in both yours and subscribed) that match the query
  searchHighlights(isbn, query, searchNotes, searchHighlights) {
    const allHighlights = this.combinedHighlights(isbn);
    return allHighlights.filter(highlight => {
      return (
        (searchHighlights &&
          highlight.selectedText.toLowerCase().indexOf(query) !== -1) ||
        (searchNotes &&
          highlight.noteText &&
          highlight.noteText.toLowerCase().indexOf(query) !== -1)
      );
    });
  },

  combinedHighlights(isbn) {
    let highlights = {};
    highlights = this.getAll(isbn);
    if (highlights.highlights) {
      return highlights.highlights.concat(highlights.subscribedHighlights);
    } else {
      return [];
    }
  },

  // Create a hash of markers to highlights
  _regenerateMarkerHash(combinedMarkers) {
    let markerHash = {};
    for (let j = 0; j < combinedMarkers.length; j++) {
      markerHash[combinedMarkers[j].globalId] = combinedMarkers[j];
    }
    this._updateState({ markerHash: markerHash });
  },

  _updateAllHighlightProperties(isbn) {
    const combinedHighlights = this.combinedHighlights(isbn);
    combinedHighlights.forEach(this._updateHighlightProperties.bind(this));
  },

  _updateHighlightProperties(highlight) {
    this._addDateToHighlight(highlight);
    highlight.userInitials = this._getUserInitials(highlight);
  },

  _getUserInitials(highlight) {
    // TODO hasn't been refactored
    var userInitials = "";
    if (highlight.subscribed && highlight.authorName) {
      var words = highlight.authorName.split(/\s+/);
      for (var i = 0; i < words.length; i++) {
        var word = words[i];
        userInitials += word.charAt(0).toUpperCase();
      }
    }
    return userInitials;
  },

  _addDateToHighlight(highlight) {
    const {
      momentLocale,
      momentFormat
    } = Fraction.Helpers.Utils.getMomentLocale();
    let lastModifiedMoment = moment(
      highlight.lastModifiedAt,
      "ddd, DD MMM YYYY HH:mm:ss ZZ"
    );
    highlight.lastModifiedString = lastModifiedMoment
      .locale(momentLocale)
      .format(momentFormat);
    return highlight;
  },

  _highlightSelection(isbn, noteText) {
    const currentMarker = MarkerStore.getCurrentMarker();
    this._highlightSelectionWithMarker(currentMarker, isbn, noteText);
  },

  _highlightSelectionWithMarker(marker, isbn, noteText) {
    let book = BookStore.get(isbn);
    book.viewer.Book.highlightCurrentSelection(
      {
        markerId: marker.globalId,
        noteText: noteText
      },
      response => {
        if (response.isSuccess()) {
          let highlights = this.getAll(book.isbn);
          Fraction.Helpers.logNPSFeedbackEvent("highlights");

          let highlight = this._addDateToHighlight(response.data.highlight);
          this._updateHighlightProperties(highlight);

          highlights._updateState({
            highlights: highlights.highlights.concat(highlight)
          });
          Dispatcher.emit(Dispatcher.HIGHLIGHT_ADDED, book.isbn, highlight);
        } else {
          // TODO tmp call bookhelper to say notes is down and trigger highlight updates
          /*
        this.notesDown = true;
        Fraction.Helpers.toast(i18n.t('reader.notebook.notes-highlights-error'), {type: "error", dismissable: true});
        bookhelper.triggerHighlightUpdates();
        */
        }
      }
    );
  },

  _deleteHighlight(isbn, syncId) {
    let book = BookStore.get(isbn);
    let highlights = this.getAll(isbn);
    book.viewer.Book.deleteHighlight(syncId, response => {
      if (response.isSuccess()) {
        let highlights = this.getAll(book.isbn);
      } else {
      }
    });
    // TODO this could probably be written better
    // Delete without waiting for callback since it's soooo slow.
    for (var i = 0; i < highlights.highlights.length; i++) {
      var curHighlight = highlights.highlights[i];
      if (curHighlight.syncId === syncId) {
        highlights.highlights.splice(i, 1);
        break;
      }
    }
    highlights._updateState({ highlights: highlights.highlights });
  },

  _updateHighlight(isbn, syncId, data) {
    if (data.noteText) {
      data.noteText = data.noteText.trim();
    }
    let book = BookStore.get(isbn);
    let highlights = this.getAll(isbn);
    book.viewer.Book.updateHighlight(syncId, data, response => {
      if (response.isSuccess()) {
        // TODO this could probably be written better
        for (var i = 0; i < highlights.highlights.length; i++) {
          var curHighlight = highlights.highlights[i];
          if (curHighlight.syncId === response.data.highlight.syncId) {
            highlights.highlights[i] = $.extend(
              true,
              highlights.highlights[i],
              response.data.highlight
            );
            this._updateHighlightProperties(highlights.highlights[i]);
            break;
          }
        }
        highlights._updateState({ highlights: highlights.highlights });
        Dispatcher.emit(
          Dispatcher.HIGHLIGHT_UPDATED,
          isbn,
          response.data.highlight
        );
      }
    });
  },

  init() {
    Dispatcher.setMaxListeners(Dispatcher.getMaxListeners() + 5);

    Dispatcher.bind(Dispatcher.HIGHLIGHT_SELECTION, (isbn, noteText) => {
      this._highlightSelection(isbn, noteText);
    });
    Dispatcher.bind(
      Dispatcher.HIGHLIGHT_EMOJI_SELECTION,
      (isbn, emoji, marker) => {
        this._highlightSelectionWithMarker(marker, isbn, emoji);
      }
    );
    Dispatcher.bind(Dispatcher.HIGHLIGHT_UPDATE, (isbn, syncId, data) => {
      this._updateHighlight(isbn, syncId, data);
    });
    Dispatcher.bind(Dispatcher.HIGHLIGHTS_DELETE, (isbn, syncId) => {
      this._deleteHighlight(isbn, syncId);
    });
    Dispatcher.bind(Dispatcher.RESET_HIGHLIGHTS, isbn => {
      this.reset(isbn);
    });
  }
};

HighlightStore.init();

export default HighlightStore;
